import { useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion, useScroll } from 'framer-motion';
import { useLayerY } from '../../hooks/useParallax';
import { StarIcon } from 'lucide-react';
import { HeadingText } from '../shared/HeadingText';
import { useCatalog } from '../../contexts/CatalogContext';
import { usePages } from '../../contexts/SiteContext';
import { rating } from '../../core/format/currency';
import { plural } from '../../core/format/text';
import { ease } from '../../core/format/motion';
import { fill } from '../../utils/copy';

/**
 * Reviews als één stem tegelijk: een groot aanhalingsteken als beeld, de quote groot gezet,
 * en eronder de namen. Kies een naam en de quote wisselt. Geen automatisch doordraaien:
 * je leest in je eigen tempo. Zonder echte reviews de kop uit Paginateksten.
 */
export function HomeReviews() {
  const pages = usePages();
  const {
    shopRating
  } = useCatalog();
  const reduce = useReducedMotion();
  const copy = pages.home.reviews;
  const quotes = pages.testimonials;
  const [index, setIndex] = useState(0);
  const count = shopRating.count;
  const avg = rating(shopRating.average);
  const current = quotes[Math.min(index, quotes.length - 1)];
  // Het aanhalingsteken beweegt langzamer dan de quote: het ligt erachter.
  const ref = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });
  const glyphY = useLayerY(scrollYProgress, [-24, 48]);
  const railRef = useRef<HTMLUListElement>(null);
  const {
    scrollXProgress: railProgress
  } = useScroll({
    container: railRef
  });
  return <section ref={ref} className="bg-sand py-20 sm:py-24 lg:py-32" aria-labelledby="home-reviews" data-id="element-7464" data-mp="1y9bbcczi9brl">
      <div className="mx-auto grid max-w-content gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-7465" data-mp="237cvl18o3h4n">
        <div className="lg:col-span-4" data-id="element-7466" data-mp="1oa3pt2imaate">
          {count > 0 ? <>
              {/* De sterren zijn beeld; de kop eronder zegt hetzelfde in woorden. */}
              <p className="flex items-center gap-1" aria-hidden="true" data-id="element-7467" data-mp="1tbjct04curef">
                {Array.from({
              length: 5
            }, (_, i) => <StarIcon key={i} className="h-4 w-4 fill-current text-clay" strokeWidth={0} data-id="element-7468" data-mp="15e3fw0bg380y" />)}
              </p>
              <h2 id="home-reviews" className="mt-4 font-display text-4xl font-light leading-[1.08] text-ink" data-id="element-7469" data-mp="10fs4vm9eyrh5">
                {avg} uit {plural(count, 'beoordeling', 'beoordelingen')}
                <span className="sr-only" data-id="element-7470" data-mp="1t2moyi2bjou3"> (van 5 sterren)</span>
              </h2>
            </> : <h2 id="home-reviews" className="font-display text-4xl font-light leading-[1.08] text-ink" data-id="element-7471" data-mp="1o5e3a7hyp2o3">
              <HeadingText heading={copy.heading} data-id="element-7472" data-mp="1j3x54jqv3ub7" />
            </h2>}
          {copy.body && <p className="mt-4 max-w-sm text-[16px] leading-relaxed text-muted" data-id="element-7473" data-mp="1e65ctomb266h">{fill(copy.body)}</p>}
        </div>

        {/*
          Telefoon: alle reviews als kaarten om doorheen te vegen (de volgende half in beeld), met een
          haarlijn die laat zien hoe ver je bent. Duim in plaats van naamknoppen zoeken.
         */}
        {quotes.length > 0 && <div className="lg:hidden" data-id="element-7474" data-mp="2jd6mikp61m7c">
            <ul ref={railRef} aria-label="Reviews" className="-mx-6 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-px-6 px-6 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-7475" data-mp="2je7ycre0acq4">
              {quotes.map(t => <li key={`${t.name}-${t.context}`} className={`shrink-0 snap-start ${quotes.length > 1 ? 'w-[86%]' : 'w-full'}`} data-id="element-7476" data-mp="2y22uyiv4x18h">
                  <figure className="flex h-full flex-col bg-shell p-6" data-id="element-7477" data-mp="326ajaexokf66">
                    <span aria-hidden="true" className="block h-8 select-none font-display text-[4.5rem] font-light leading-[0.9] text-clay" data-id="element-7478" data-mp="37428v2aurp77">
                      “
                    </span>
                    <blockquote className="mt-2 font-display text-[1.4rem] font-light leading-[1.3] text-ink" data-id="element-7479" data-mp="1cqt5ls8wgzxe">{fill(t.quote)}</blockquote>
                    <figcaption className="mt-auto pt-6 text-[14px] text-muted" data-id="element-7480" data-mp="1hqfj82sn4i6q">
                      {t.name} · {t.context}
                    </figcaption>
                  </figure>
                </li>)}
            </ul>
            {quotes.length > 1 && <div aria-hidden="true" className="mt-6 h-px w-full bg-ink/15" data-id="element-7481" data-mp="3h5ldr3qulzlq">
                <motion.div style={{
            scaleX: railProgress
          }} className="h-px origin-left bg-ink" data-id="element-7482" data-mp="3d64746zoqwez" />
              </div>}
          </div>}

        {current && <div className="hidden lg:col-span-8 lg:block" data-id="element-7483" data-mp="2kgx00jm60yi4">
            {/* Het aanhalingsteken als beeld; in de flow, zodat het nooit over de kop erboven valt. */}
            <motion.span aria-hidden="true" style={glyphY ? {
          y: glyphY
        } : undefined} className="block h-12 select-none font-display text-[7rem] font-light leading-[0.9] text-clay sm:h-16 sm:text-[9rem]" data-id="element-7484" data-mp="2tgttcyji2lji">
              “
            </motion.span>
            <div aria-live="polite" className="min-h-[13rem] sm:min-h-[15rem]" data-id="element-7485" data-mp="1rlpgn5bdql7u">
              <AnimatePresence mode="wait" initial={false} data-id="element-7486" data-mp="1rjmfpq8ov7oc">
                <motion.figure key={index} initial={reduce ? {
              opacity: 0
            } : {
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.22,
              ease
            }} data-id="element-7487" data-mp="1wdh4v972ceut">
                  <blockquote className="font-display text-[1.9rem] font-light leading-[1.22] tracking-[-0.01em] text-ink sm:text-[2.5rem] lg:text-[2.8rem]" data-id="element-7488" data-mp="2adi8gwepq0dz">
                    {fill(current.quote)}
                  </blockquote>
                  <figcaption className="mt-6 text-[14px] text-muted" data-id="element-7489" data-mp="20g3ssz0m5fmq">
                    {current.name} · {current.context}
                  </figcaption>
                </motion.figure>
              </AnimatePresence>
            </div>

            {quotes.length > 1 && <ul aria-label="Kies een review" className="mt-10 flex flex-wrap gap-x-6 gap-y-2 border-t border-ink/15 pt-5" data-id="element-7490" data-mp="tyqhmnx7t16s">
                {quotes.map((t, i) => {
            const isActive = i === index;
            return <li key={`${t.name}-${t.context}`} data-id="element-7491" data-mp="2mut0xakn9yei" data-mpi={i}>
                      <button type="button" aria-pressed={isActive} onClick={() => setIndex(i)} className={`relative min-h-[44px] whitespace-nowrap text-[15px] transition-colors duration-150 ease-soft ${isActive ? 'text-ink' : 'text-muted hover:text-ink'}`} data-id="element-7492" data-mp="2mt857hmlg9f2">
                        {t.name}
                        {isActive && <motion.span layoutId="review-marker" aria-hidden="true" className="absolute inset-x-0 -top-[21px] h-px bg-ink" transition={{
                  duration: reduce ? 0 : 0.25,
                  ease
                }} data-id="element-7493" data-mp="1yx3t86ti1t0i" />}
                      </button>
                    </li>;
          })}
              </ul>}
          </div>}
      </div>
    </section>;
}